import { get, kebabCase } from 'lodash';
import React, { Fragment } from 'react';
import { P, Small } from '@upsolve/ui';

import * as HEADERS from '../Type/Display.h';
import StateMedianIncomeChart from '../Programmatic/StateMedianIncomeChart';
import StatePovertyLevelsChart from '../Programmatic/StatePovertyLevelsChart';
import DistrictRequirementsSection from '../Programmatic/DistrictRequirementsSection';
import LegalAidOrgsList from '../Programmatic/LegalAidOrgsList';
import CourtsList from '../Programmatic/CourtsList';
import JudgesList from '../Programmatic/JudgesList';
import TrusteesList from '../Programmatic/TrusteesList';
import CTAInsertSimple from '../Programmatic/CTAInsertSimple';
import CTAInsertZipcode from '../Programmatic/CTAInsertZipcode';
import CityZipcodeTable from '../Programmatic/CityZipcodeTable';
import LegalServiceListing from '../Programmatic/LegalServiceListing';
import ImageInContext from '../Media/ImageInContext';
import HorizontalRule from '../Layout/HorizontalRule.hr';
import privatePricings from '../../../content/privatePricings.json';

export default function richTextNodeToComponent(node, nodeIndex, nodeArray, context) {
  if (node == null) return null;
  const locale = 'en-US';
  const { data, content, marks, nodeType, value } = node;
  const locality = get(context, 'locality');

  // If all text, concat elements without marks
  const fullText = node.content != null && node.content.find(c => c.nodeType !== 'text') == null
    ? node.content.reduce((acc, c) => `${acc}${c.value}` , '')
    : null;

  // If document type, idk, just return content
  if (nodeType === 'document') {
    return Array.prototype.map.call(node.content, (n, i, a) => richTextNodeToComponent(n, i, a, context));
  }

  // HTML Elements
  // - Text
  if (nodeType === 'text' && (value || '').trim().length > 0) {
    if (marks.find(m => m.type === 'bold') != null && marks.find(m => m.type === 'italic')) {
      return <b key={nodeIndex}><i>{value}</i></b>;
    }
    if (marks.find(m => m.type === 'bold') != null) {
      return <b key={nodeIndex}>{value}</b>;
    }
    if (marks.find(m => m.type === 'italic') != null) {
      return <i key={nodeIndex}>{value}</i>;
    }
    return value;
  }
  // - Links
  if (nodeType === 'hyperlink') {
    return (
      <a href={data.uri} key={nodeIndex}>
        {Array.prototype.map.call(content, (n, i, a) => richTextNodeToComponent(n, i, a, context))}
      </a>
    );
  }
  // - Headers
  if (nodeType.includes('heading-')) {
    const H = HEADERS[`H${nodeType.slice(-1)}`];
    return get(content, '[0].value') != null
      ? (
        <H id={kebabCase(fullText)} key={nodeIndex}>
          {Array.prototype.map.call(node.content, (n, i, a) => richTextNodeToComponent(n, i, a, context))}
        </H>
      )
      : null;
  }
  // - Paragraph
  if (nodeType === 'paragraph' && node.content.filter(c => c.nodeType === 'hyperlink' || (c.nodeType === 'text' && c.value.length > 0)).length) {
    return (
      <P key={nodeIndex}>
        {Array.prototype.map.call(content, (n, i, a) => richTextNodeToComponent(n, i, a, context))}
      </P>
    )
  }
  // - Lists
  if (nodeType === 'list-item') {
    // Inner content is probably a paragraph
    // HACK: Dear God.
    return <li key={nodeIndex}>{Array.prototype.map.call(content, (n, i, a) => richTextNodeToComponent(n, i, a, context))}</li>
  }
  if (nodeType.includes('-list')) {
    const listItems = Array.prototype.map.call(content, (n, i, a) => richTextNodeToComponent(n, i, a, context));
    return nodeType.includes('unordered')
      ? <ul key={nodeIndex}>{listItems}</ul>
      : <ol key={nodeIndex}>{listItems}</ol>;
  }

  // Embeded Assets
  // - Images
  if (nodeType === 'embedded-asset-block') {
    return get(data, "target.fields.file['en-US'].url") != null
      ? (
        <ImageInContext
          key={nodeIndex}
          alt={get(data, "target.fields.description['en-US']") || get(data, "target.fields.title['en-US']")}
          src={get(data, "target.fields.file['en-US'].url")}
        />
      )
      : null;
  }

  // Inline Embeded Entries
  if (nodeType === 'embedded-entry-inline') {
    // - Year
    if (get(data, "target.fields.type['en-US']") === 'year') {
      return String(new Date().getUTCFullYear());
    }
    // - Attorney Cost Low
    if (get(data, "target.fields.type['en-US']") === 'attorneyPriceLow') {
      return `$${Number((privatePricings[get(locality, 'state', '').toUpperCase()] || {}).low || 500).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    // - Attorney Cost High
    if (get(data, "target.fields.type['en-US']") === 'attorneyPriceHigh') {
      return `$${Number((privatePricings[get(locality, 'state', '').toUpperCase()] || {}).high || 1500).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }

  // Embeded Entries
  if (nodeType === 'embedded-entry-block') {
    // - Image
    if (get(data, 'target.sys.contentType.sys.id') === 'image') {
      return (
        <Fragment>
          <ImageInContext
            key={nodeIndex}
            alt={get(data, "target.fields.altText['en-US']") || get(data, "target.fields.title['en-US']") || get(data, "target.fields.caption['en-US']")}
            src={get(data, "target.fields.image['en-US'].fields.file['en-US'].url")}
          />
          {get(data, "target.fields.caption['en-US']") && (
            <div className="image-caption">
              <Small>{get(data, "target.fields.caption['en-US']")}</Small>
            </div>
          )}
        </Fragment>
      );
    }
    // - FAQ
    if (get(data, 'target.sys.contentType.sys.id') === 'faq') {
      const H = HEADERS.H4;
      const quetionHeaders = get(data, "target.fields.questions['en-US']", [])
        .map(q => get(q, "fields.text['en-US']"))
      return (
        <div key={nodeIndex}>
          <div>
            <ul>
              {Array.prototype.map.call(quetionHeaders, (q, i) => <li key={i}><a href={`#${kebabCase(q)}`}>{q}</a></li>)}
            </ul>
          </div>
          <HorizontalRule />
          {Array.prototype.map.call(get(data, "target.fields.questions['en-US']"), (question, questionIndex, questions) => {
            return (
              <section key={questionIndex}>
                <H as="h2" id={kebabCase(get(question, "fields.text['en-US']"))}>{get(question, "fields.text['en-US']")}</H>
                {richTextNodeToComponent(get(question, "fields.answers['en-US'][0].fields.text['en-US']"), 0, [], context)}
                {(questionIndex !== 0 && questions.length > 1)
                  && <small className="back-to-top"><a href="#top">↑ Back to top</a></small>}
              </section>
            );
          })}
        </div>
      );
    }
    // - How To
    if (get(data, 'target.sys.contentType.sys.id') === 'howTo') {
      const H = HEADERS.H4;
      const steps = get(node, `data.target.fields.steps['en-US']`, []).map(step => step.fields);
      return steps.length > 0
        ? (
          <div key={nodeIndex}>
            <div>
              <ol>
                {Array.prototype.map.call(steps, (step, i) => (
                  <li key={i}><a href={`#${kebabCase(step.title['en-US'])}`}>{step.title['en-US']}</a></li>
                ))}
              </ol>
            </div>
            <HorizontalRule />
            {Array.prototype.map.call(steps, (step, stepIndex) => {
              return (
                <div key={stepIndex} className="section">
                  <H as="h3" id={kebabCase(step.title['en-US'])}>{step.title['en-US']}</H>
                  {richTextNodeToComponent(get(step, "content['en-US']"), 0, [], context)}
                </div>
              );
            })}
          </div>
        )
        : null;
    }
    // - Legal Service
    if (get(data, 'target.sys.contentType.sys.id') === 'legalService') {
      return (
        <LegalServiceListing
          businessName={get(data, `target.fields.businessName['${locale}']`)}
          firstName={get(data, `target.fields.firstName['${locale}']`)}
          lastName={get(data, `target.fields.lastName['${locale}']`)}
          email={get(data, `target.fields.email['${locale}']`)}
          phoneNumber={get(data, `target.fields.phoneNumber['${locale}']`)}
          image={get(data, `target.fields.image['${locale}'].fields.image['${locale}'].fields.file['${locale}'].url`)}
          address={{
            street1: get(data, `target.fields.address[${locale}].fields.street1[${locale}]`),
            street2: get(data, `target.fields.address[${locale}].fields.street2[${locale}]`),
            city: get(data, `target.fields.address[${locale}].fields.city[${locale}]`),
            state: get(data, `target.fields.address[${locale}].fields.state[${locale}]`),
            zipcode: get(data, `target.fields.address[${locale}].fields.zipcode[${locale}]`),
          }}
          biography={get(data, `target.fields.description['${locale}']`)}
        />
      );
    }
    // - Zipcodes Table
    if (get(data, "target.fields.type['en-US']") === 'cityZipcodeTable') {
      return <CityZipcodeTable key={nodeIndex} state={get(locality, 'state')} city={get(locality, 'city')} />
    }
    // - Median Income Chart
    if (get(data, "target.fields.type['en-US']") === 'stateMedianFamilyIncome') {
      return <StateMedianIncomeChart key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    // - Poverty Levels Chart
    if (get(data, "target.fields.type['en-US']") === 'statePovertyLevel') {
      return <StatePovertyLevelsChart key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    // - District Requirements
    if (get(data, "target.fields.type['en-US']") === 'districtRequirements') {
      return <DistrictRequirementsSection key={nodeIndex} district={get(data, "target.fields.district['en-US']")} />
    }
    // - Legal Aid Org List
    if (get(data, "target.fields.type['en-US']") === 'stateLegalAidOrganizations') {
      return <LegalAidOrgsList key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    // - Courts List
    if (get(data, "target.fields.type['en-US']") === 'stateCourts') {
      return <CourtsList key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    if (get(data, "target.fields.type['en-US']") === 'districtCourts') {
      return <CourtsList key={nodeIndex} district={get(data, "target.fields.district['en-US']")} />
    }
    // - Judges List
    if (get(data, "target.fields.type['en-US']") === 'stateJudges') {
      return <JudgesList key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    if (get(data, "target.fields.type['en-US']") === 'districtJudges') {
      return <JudgesList key={nodeIndex} district={get(data, "target.fields.district['en-US']")} />
    }
    // - Trustees List
    if (get(data, "target.fields.type['en-US']") === 'stateTrustees') {
      return <TrusteesList key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    // - CTA Simple
    if (get(data, "target.fields.type['en-US']") === 'ctaSimpleInsert') {
      return <CTAInsertSimple key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
    // - CTA Zipcode
    if (get(data, "target.fields.type['en-US']") === 'ctaZipcode') {
      return <CTAInsertZipcode key={nodeIndex} state={get(data, "target.fields.state['en-US']") || get(locality, 'state')} />
    }
  }

  return null;
}
